import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUsers, FiClipboard, FiRepeat, FiMessageSquare, FiCheckCircle, FiAlertTriangle, FiClock, FiTrendingUp, FiActivity, FiZap, FiArrowRight, FiBarChart2, FiServer, FiChevronLeft, FiChevronRight, FiRefreshCw, FiCalendar, } from 'react-icons/fi';
import { ROUTES } from '../../app/routes';
import { fetchArtifactsSummary, fetchArtifactsActivity, type ArtifactsSummaryResponse, type ArtifactsActivityResponse, } from '../claudeagents/claudeAgentApi';
import { resolveTaskTransactionId } from '../../services/kanbanAIService';
import '../../styles/DwArtifacts.css';
import '../../styles/Artifacts.css';
import '../../styles/MomentumCommand.css';
type DatePreset = 'today' | 'week' | 'month';
const DATE_PRESETS: Array<{
    key: DatePreset;
    label: string;
}> = [
    { key: 'today', label: 'Today' },
    { key: 'week', label: 'Last Week' },
    { key: 'month', label: 'Last Month' },
];
function fmtDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
function presetDates(preset: DatePreset): {
    from: string;
    to: string;
} {
    const today = new Date();
    const from = new Date(today);
    if (preset === 'week')
        from.setDate(today.getDate() - 6);
    else if (preset === 'month')
        from.setDate(today.getDate() - 29);
    return { from: fmtDate(from), to: fmtDate(today) };
}
function chatSessionTitle(title?: string): string {
    const trimmed = (title ?? '').trim();
    if (!trimmed || /^\[Runtime context\]/i.test(trimmed))
        return 'Untitled conversation';
    return trimmed.split('\n')[0].trim() || 'Untitled conversation';
}
const KANBAN_STATUS_GUID: Record<string, string> = {
    '11111111-1111-1111-1111-111111111111': 'todo',
    '22222222-2222-2222-2222-222222222222': 'in_progress',
    '33333333-3333-3333-3333-333333333333': 'in_review',
    '44444444-4444-4444-4444-444444444444': 'done',
};
const STATUS_MAP: Record<string, {
    label: string;
    color: string;
}> = {
    todo: { label: 'To Do', color: '#6366f1' },
    pending: { label: 'To Do', color: '#6366f1' },
    unknown: { label: 'Unknown', color: '#6366f1' },
    in_progress: { label: 'In Progress', color: '#f59e0b' },
    running: { label: 'Running', color: '#f59e0b' },
    in_review: { label: 'In Review', color: '#00b0d3' },
    done: { label: 'Done', color: '#22c55e' },
    completed: { label: 'Done', color: '#22c55e' },
    success: { label: 'Done', color: '#22c55e' },
    failed: { label: 'Failed', color: '#ef4444' },
    error: { label: 'Failed', color: '#ef4444' },
};
function statusBadge(raw?: string) {
    const key = (raw || 'unknown').toLowerCase().replace(/\s+/g, '_');
    const m = STATUS_MAP[key] || { label: raw || 'Unknown', color: '#64748b' };
    return m;
}
function timeAgo(dateStr?: string): string {
    if (!dateStr)
        return '—';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1)
        return 'just now';
    if (mins < 60)
        return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)
        return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30)
        return `${days}d ago`;
    return `${Math.floor(days / 30)}mo ago`;
}
function fmtDuration(sec?: number): string {
    if (!sec)
        return '—';
    if (sec < 60)
        return `${sec}s`;
    return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}
function taskStatusFromId(statusId?: string | null): string {
    return KANBAN_STATUS_GUID[statusId || ''] || statusId || '';
}
const AVATAR_COLORS = ['#8b5cf6', '#00b0d3', '#f59e0b', '#22c55e', '#ef4444', '#6366f1', '#ec4899', '#14b8a6'];
const DwArtifactsPage: React.FC = () => {
    const navigate = useNavigate();
    const [summary, setSummary] = useState<ArtifactsSummaryResponse | null>(null);
    const [activity, setActivity] = useState<ArtifactsActivityResponse | null>(null);
    const [loading, setLoading] = useState(true);         // Phase 1 (cards + stats)
    const [activityLoading, setActivityLoading] = useState(true); // Phase 2 (tables + feeds)
    const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
    const [agentPage, setAgentPage] = useState(0);
    const AGENTS_PER_PAGE = 4;
    const [dateFrom, setDateFrom] = useState<string>(() => presetDates('week').from);
    const [dateTo, setDateTo] = useState<string>(() => presetDates('week').to);
    const [showDateFilter, setShowDateFilter] = useState(false);
    const dateFilterRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (dateFilterRef.current && !dateFilterRef.current.contains(e.target as Node)) {
                setShowDateFilter(false);
            }
        }
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);
    const activeDatePreset: DatePreset | null = useMemo(() => {
        if (!dateFrom || !dateTo)
            return null;
        const today = fmtDate(new Date());
        if (dateTo !== today)
            return null;
        if (dateFrom === today)
            return 'today';
        if (dateFrom === presetDates('week').from)
            return 'week';
        if (dateFrom === presetDates('month').from)
            return 'month';
        return null;
    }, [dateFrom, dateTo]);
    const applyDatePreset = useCallback((preset: DatePreset) => {
        const { from, to } = presetDates(preset);
        setDateFrom(from);
        setDateTo(to);
    }, []);
    const clearDateFilters = useCallback(() => {
        setDateFrom('');
        setDateTo('');
    }, []);
    const dateFilterSummary = useMemo(() => {
        if (activeDatePreset === 'today')
            return 'Today';
        if (activeDatePreset === 'week')
            return 'Last Week';
        if (activeDatePreset === 'month')
            return 'Last Month';
        if (dateFrom && dateTo) {
            return `${new Date(dateFrom + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(dateTo + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
        }
        return 'All dates';
    }, [activeDatePreset, dateFrom, dateTo]);
    const fetchData = useCallback(async (cancelledRef: { current: boolean }) => {
        setLoading(true);
        setActivityLoading(true);
        const df = dateFrom || undefined;
        const dt = dateTo || undefined;

        // Fire both phases in parallel — phase 1 renders first
        const summaryP = fetchArtifactsSummary(df, dt).catch(() => null);
        const activityP = fetchArtifactsActivity(df, dt).catch(() => null);

        // Phase 1 arrives → page shell renders with agent cards + stats
        const summaryData = await summaryP;
        if (cancelledRef.current) return;
        if (summaryData) setSummary(summaryData);
        setLoading(false);

        // Phase 2 arrives → card feeds + tables fill in
        const activityData = await activityP;
        if (cancelledRef.current) return;
        if (activityData) setActivity(activityData);
        setActivityLoading(false);
    }, [dateFrom, dateTo]);
    useEffect(() => {
        const cancelledRef = { current: false };
        fetchData(cancelledRef);
        return () => { cancelledRef.current = true; };
    }, [fetchData]);
    const handleRefresh = useCallback(() => {
        if (loading)
            return;
        fetchData({ current: false });
    }, [fetchData, loading]);
    // ── Phase 1 data (renders immediately) ──────────────────────────────────
    const agents = summary?.agents ?? [];
    const stats = summary?.stats;
    const totalAgents = stats?.total_agents ?? 0;
    const premadeCount = stats?.premade_count ?? 0;
    const customCount = stats?.custom_count ?? 0;
    const totalTasks = stats?.total_tasks ?? 0;
    const totalRuns = stats?.total_runs ?? 0;
    const totalChats = stats?.total_chats ?? 0;
    const successRuns = stats?.success_runs ?? 0;
    const failedRuns = stats?.failed_runs ?? 0;
    const overallSuccessRate = stats?.success_rate ?? 0;

    // Agent cards — phase 1 renders counts/stats; phase 2 fills activity feeds
    const agentFeeds = activity?.agent_feeds ?? {};
    const agentBreakdown = useMemo(() =>
        agents.map((a, i) => {
            const feed = (agentFeeds[a.agent_id] ?? []).map(f => ({
                type: f.type,
                title: f.title || (f.type === 'chat' ? 'Untitled conversation' : 'Untitled'),
                status: f.type === 'task' ? (KANBAN_STATUS_GUID[f.status_id || ''] || f.status_id || '') : (f.status || ''),
                time: timeAgo(f.time || undefined),
                raw: f.time || '',
                taskNumber: f.task_number || undefined,
                navId: f.type === 'task' ? String(f.id) : f.type === 'run' ? (f.routine_id || undefined) : undefined,
                chatId: f.type === 'chat' ? String(f.id) : undefined,
                agentId: a.agent_id,
            }));
            return {
                agent_id: a.agent_id, name: a.name,
                is_system: a.is_system, is_premade: a.is_system,
                is_primary_agent: a.is_primary_agent, is_voice_agent: a.is_voice_agent,
                color: AVATAR_COLORS[i % AVATAR_COLORS.length],
                initials: a.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2),
                tasks: a.counts.kanban_tasks, runs: a.counts.routine_runs, chats: a.counts.chats,
                totalRuns: a.counts.routine_runs, successRuns: a.counts.success_runs,
                failedRuns: a.counts.failed_runs, successRate: a.counts.success_rate,
                latestFeed: feed,
                lastActive: a.last_active ? timeAgo(a.last_active) : '—',
                lastActiveRaw: a.last_active || '',
            };
        }).sort((a, b) => {
            const bTime = b.lastActiveRaw ? new Date(b.lastActiveRaw).getTime() : 0;
            const aTime = a.lastActiveRaw ? new Date(a.lastActiveRaw).getTime() : 0;
            return bTime - aTime;
        }),
    [agents, agentFeeds]);

    // ── Phase 2 data (fills in tables + feeds) ──────────────────────────────
    const recentActivity = useMemo(() =>
        (activity?.recent_activity ?? []).map(item => ({
            type: item.type,
            title: item.title || (item.type === 'chat' ? 'Untitled conversation' : 'Untitled'),
            agent: item.agent_name || '',
            status: item.type === 'task' ? (KANBAN_STATUS_GUID[item.status_id || ''] || item.status_id || '') : (item.status || ''),
            time: timeAgo(item.time || undefined),
            raw: item.time || '',
            taskNumber: item.task_number || undefined,
            navId: item.type === 'task' ? String(item.id) : item.type === 'run' ? (item.routine_id || undefined) : undefined,
            chatId: item.type === 'chat' ? String(item.id) : undefined,
        })),
    [activity]);

    const allCommands = activity?.commands ?? [];
    const allRuns2 = activity?.runs ?? [];
    const allChats2 = activity?.chats ?? [];
    const filteredTasks = selectedAgent ? allCommands.filter(t => t.agent_id === selectedAgent) : allCommands;
    const filteredRuns = selectedAgent ? allRuns2.filter(r => r.agent_id === selectedAgent) : allRuns2;
    const filteredChats = selectedAgent ? allChats2.filter(c => c.agent_id === selectedAgent) : allChats2;
    const handleGoToDW = useCallback(() => navigate(ROUTES.DIGITAL_WORKFORCE), [navigate]);
    return (<div className="dwa">
      
      <div className="dwa-header">
        <div className="dwa-header__left">
          <div className="dwa-header__icon"><FiActivity size={20}/></div>
          <div>
            <h1 className="dwa-header__title">Agent Dashboard</h1>
            <p className="dwa-header__subtitle">Digital Workforce analytics, performance & activity dashboard</p>
          </div>
        </div>
        <div className="dwa-header__actions">
          {agentBreakdown.length > 0 && (<>
              <span className="dwa-agent-cards-nav__info">{agentPage * AGENTS_PER_PAGE + 1}–{Math.min((agentPage + 1) * AGENTS_PER_PAGE, agentBreakdown.length)} of {agentBreakdown.length}</span>
              <button className="dwa-agent-cards-nav__btn" disabled={agentPage === 0} onClick={() => setAgentPage(p => p - 1)}><FiChevronLeft size={14}/></button>
              <button className="dwa-agent-cards-nav__btn" disabled={(agentPage + 1) * AGENTS_PER_PAGE >= agentBreakdown.length} onClick={() => setAgentPage(p => p + 1)}><FiChevronRight size={14}/></button>
            </>)}
          
          <div ref={dateFilterRef} style={{ position: 'relative' }}>
            <button type="button" className={`art-filter__btn art-filter__btn--date${(showDateFilter || dateFrom || dateTo) ? ' art-filter__btn--active' : ''}`} onClick={() => setShowDateFilter(v => !v)} title="Filter by date">
              <FiCalendar size={12}/>
              <span className="art-filter__btn-label">{dateFilterSummary}</span>
            </button>
            {showDateFilter && (<div className="art-filter__popover art-filter__popover--date">
                <div className="art-filter__type-header">
                  <span className="art-filter__label">Filter by date</span>
                  {(dateFrom || dateTo) && (<button type="button" className="art-filter__clear" onClick={clearDateFilters}>
                      Clear
                    </button>)}
                </div>
                <div className="art-filter__preset-pills">
                  {DATE_PRESETS.map(p => (<button key={p.key} type="button" className={`art-filter__preset-pill${activeDatePreset === p.key ? ' art-filter__preset-pill--active' : ''}`} onClick={() => applyDatePreset(p.key)}>
                      {p.label}
                    </button>))}
                </div>
                <div className="art-filter__custom-dates">
                  <div className="art-filter__date-row">
                    <label className="art-filter__label">From</label>
                    <input className="art-filter__date" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}/>
                  </div>
                  <div className="art-filter__date-row">
                    <label className="art-filter__label">To</label>
                    <input className="art-filter__date" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}/>
                  </div>
                </div>
              </div>)}
          </div>
          <button className="mc-btn-icon" onClick={handleRefresh} disabled={loading} title="Refresh">
            <FiRefreshCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : undefined}/>
          </button>
          <button className="dwa-header__btn" onClick={handleGoToDW}>
            <FiUsers size={14}/> Manage Digital Workforce <FiArrowRight size={12}/>
          </button>
        </div>
      </div>

      {loading ? (<>
          
          <div className="dwa-hero-row">
            {[1, 2, 3, 4].map(i => (<div className="dwa-hero-card" key={i}>
                <div className="dwa-skel dwa-skel--avatar"/>
                <div className="dwa-hero-card__body dwa-hero-card__body--skeleton">
                  <div className="dwa-skel dwa-skel--line-title"/>
                  <div className="dwa-skel dwa-skel--line-subtitle"/>
                </div>
              </div>))}
          </div>

          
          <div className="dwa-agent-cards-section">
            <div className="dwa-agent-cards-nav">
              <span className="dwa-agent-cards-nav__label">Agents</span>
            </div>
            <div className="dwa-agent-cards-row">
              {[1, 2, 3, 4].map(i => (<div className="dwa-spotlight" key={i}>
                  <div className="dwa-spotlight__top">
                    <div className="dwa-skel dwa-skel--spotlight-avatar"/>
                    <div className="dwa-spotlight__identity dwa-spotlight__identity--skeleton">
                      <div className="dwa-skel dwa-skel--spotlight-name"/>
                      <div className="dwa-skel dwa-skel--spotlight-meta"/>
                    </div>
                  </div>
                  <div className="dwa-skel dwa-skel--spotlight-stats"/>
                  <div className="dwa-skel dwa-skel--spotlight-feed"/>
                </div>))}
            </div>
          </div>

          
          <div className="dwa-grid">
            <div className="dwa-section dwa-section--activity">
              <div className="dwa-skel dwa-skel--section-activity"/>
            </div>
            <div className="dwa-section dwa-section--tasks">
              <div className="dwa-skel dwa-skel--section-lg"/>
            </div>
            <div className="dwa-section dwa-section--runs">
              <div className="dwa-skel dwa-skel--section-lg"/>
            </div>
            <div className="dwa-section dwa-section--chats">
              <div className="dwa-skel dwa-skel--section-lg"/>
            </div>
          </div>
        </>) : (<>
          
          {agentBreakdown.length > 0 && (<div className="dwa-agent-cards-section">
            <div className="dwa-agent-cards-nav">
              <span className="dwa-agent-cards-nav__label">Agents</span>
            </div>
            <div className="dwa-agent-cards-row">
            {agentBreakdown.slice(agentPage * AGENTS_PER_PAGE, (agentPage + 1) * AGENTS_PER_PAGE).map(a => (<div key={a.agent_id} className={`dwa-spotlight${selectedAgent === a.agent_id ? ' dwa-spotlight--selected' : ''}`} onClick={() => setSelectedAgent(selectedAgent === a.agent_id ? null : a.agent_id)}>
                
                <div className="dwa-spotlight__top">
                  <div className="dwa-spotlight__avatar" style={{ background: a.color }}>{a.initials}</div>
                  <div className="dwa-spotlight__identity">
                    <span className="dwa-spotlight__name">{a.name}</span>
                    <span className="dwa-spotlight__meta">
                      <span className="dwa-spotlight__type">{a.is_premade || a.is_system ? 'Default' : 'Custom'}</span>
                      <span className="dwa-spotlight__dot"/>
                      <span className="dwa-spotlight__last-active">Active {a.lastActive}</span>
                    </span>
                  </div>
                  {a.successRate > 0 && (<div className="dwa-spotlight__rate" style={{ color: a.successRate >= 80 ? '#22c55e' : a.successRate >= 50 ? '#f59e0b' : '#ef4444' }}>
                      {a.successRate}%
                    </div>)}
                </div>

                
                <div className="dwa-spotlight__stats">
                  <div className="dwa-spotlight__stat">
                    <FiClipboard size={11} style={{ color: a.color }}/>
                    <span className="dwa-spotlight__stat-val">{a.tasks}</span>
                    <span className="dwa-spotlight__stat-lbl">Commands</span>
                  </div>
                  <div className="dwa-spotlight__stat">
                    <FiRepeat size={11} style={{ color: '#00b0d3' }}/>
                    <span className="dwa-spotlight__stat-val">{a.runs}</span>
                    <span className="dwa-spotlight__stat-lbl">Runs</span>
                  </div>
                  <div className="dwa-spotlight__stat">
                    <FiMessageSquare size={11} style={{ color: '#8b5cf6' }}/>
                    <span className="dwa-spotlight__stat-val">{a.chats}</span>
                    <span className="dwa-spotlight__stat-lbl">Chats</span>
                  </div>
                </div>

                
                {a.latestFeed.length > 0 && (<div className="dwa-spotlight__feed">
                    <span className="dwa-spotlight__feed-title">Latest Activity</span>
                    {a.latestFeed.map((f, fi) => {
                            const s = f.status ? statusBadge(f.status) : null;
                            const clickable = ((f.type === 'task' || f.type === 'run') && !!f.navId) || (f.type === 'chat' && !!f.chatId);
                            const goTo = async (e: React.MouseEvent) => {
                                e.stopPropagation();
                                if (f.type === 'task' && f.navId) {
                                    const txId = await resolveTaskTransactionId(f.navId);
                                    navigate(txId
                                        ? `${ROUTES.MOMENTUM_COMMAND}/${encodeURIComponent(txId)}`
                                        : `${ROUTES.MOMENTUM_COMMAND}?taskId=${f.navId}`);
                                }
                                else if (f.type === 'run' && f.navId)
                                    navigate(`${ROUTES.MOMENTUM_ROUTINES}/${encodeURIComponent(f.navId)}`);
                                else if (f.type === 'chat' && f.chatId)
                                    navigate(`${ROUTES.CHAT}/${f.chatId}`, {
                                        state: { dwAgentId: f.agentId, dwAgentName: a.name, dwSessionId: f.chatId },
                                    });
                            };
                            return (<div key={fi} className={`dwa-spotlight__feed-item${clickable ? ' dwa-spotlight__feed-item--clickable' : ''}`} onClick={clickable ? goTo : undefined}>
                          <span className={`dwa-spotlight__feed-icon dwa-spotlight__feed-icon--${f.type}`}>
                            {f.type === 'task' ? <FiClipboard size={9}/> : f.type === 'run' ? <FiRepeat size={9}/> : <FiMessageSquare size={9}/>}
                          </span>
                          <span className="dwa-spotlight__feed-text">
                            {f.taskNumber && <span className="dwa-table__task-num">{f.taskNumber}</span>}
                            {f.title}
                          </span>
                          {s && <span className="dwa-spotlight__feed-badge" style={{ color: s.color }}>{s.label}</span>}
                          <span className="dwa-spotlight__feed-time">{f.time}</span>
                        </div>);
                        })}
                  </div>)}

                
                {a.totalRuns > 0 && (<div className="dwa-spotlight__bar-wrap">
                    <div className="dwa-spotlight__bar">
                      <div className="dwa-spotlight__bar-fill" style={{ width: `${a.successRate}%`, background: a.successRate >= 80 ? '#22c55e' : a.successRate >= 50 ? '#f59e0b' : '#ef4444' }}/>
                    </div>
                    <span className="dwa-spotlight__bar-label">{a.successRuns}/{a.totalRuns} runs passed</span>
                  </div>)}
              </div>))}
            </div>
          </div>)}


          <div className="dwa-grid">

            {activityLoading ? (<>
              <div className="dwa-section dwa-section--activity"><div className="dwa-skel dwa-skel--section-activity"/></div>
              <div className="dwa-section dwa-section--tasks"><div className="dwa-skel dwa-skel--section-lg"/></div>
              <div className="dwa-section dwa-section--runs"><div className="dwa-skel dwa-skel--section-lg"/></div>
              <div className="dwa-section dwa-section--chats"><div className="dwa-skel dwa-skel--section-lg"/></div>
            </>) : (<>

            <div className="dwa-section dwa-section--activity">
              <div className="dwa-section__hdr">
                <FiZap size={14}/>
                <span>Recent Activity</span>
                <span className="dwa-section__badge">{recentActivity.length}</span>
              </div>
              <div className="dwa-activity-list">
                {recentActivity.length === 0 ? (<div className="dwa-empty">No activity yet. Assign tasks or run routines to see activity here.</div>) : recentActivity.map((item, i) => {
                const s = item.status ? statusBadge(item.status) : null;
                const clickable = ((item.type === 'task' || item.type === 'run') && !!item.navId) || (item.type === 'chat' && !!item.chatId);
                const goTo = async () => {
                    if (item.type === 'task' && item.navId) {
                        const txId = await resolveTaskTransactionId(item.navId);
                        navigate(txId
                            ? `${ROUTES.MOMENTUM_COMMAND}/${encodeURIComponent(txId)}`
                            : `${ROUTES.MOMENTUM_COMMAND}?taskId=${item.navId}`);
                    }
                    else if (item.type === 'run' && item.navId)
                        navigate(`${ROUTES.MOMENTUM_ROUTINES}/${encodeURIComponent(item.navId)}`);
                    else if (item.type === 'chat' && item.chatId)
                        navigate(`${ROUTES.CHAT}/${item.chatId}`, {
                            state: { dwAgentId: undefined, dwAgentName: item.agent, dwSessionId: item.chatId },
                        });
                };
                return (<div key={i} className={`dwa-activity-item${clickable ? ' dwa-activity-item--clickable' : ''}`} onClick={clickable ? goTo : undefined}>
                      <div className={`dwa-activity-item__icon dwa-activity-item__icon--${item.type}`}>
                        {item.type === 'task' ? <FiClipboard size={12}/> : item.type === 'run' ? <FiRepeat size={12}/> : <FiMessageSquare size={12}/>}
                      </div>
                      <div className="dwa-activity-item__body">
                        <span className="dwa-activity-item__title">
                          {item.taskNumber && <span className="dwa-table__task-num">{item.taskNumber}</span>}
                          {item.title}
                        </span>
                        <span className="dwa-activity-item__meta">
                          <span className="dwa-activity-item__agent">{item.agent}</span>
                          <span className="dwa-activity-item__time">{item.time}</span>
                        </span>
                      </div>
                      {s && (<span className="dwa-activity-item__badge" style={{ color: s.color, background: `${s.color}16`, borderColor: `${s.color}33` }}>
                          {s.label}
                        </span>)}
                    </div>);
            })}
              </div>
            </div>

            
            <div className="dwa-section dwa-section--tasks">
              <div className="dwa-section__hdr">
                <FiClipboard size={14}/>
                <span>Assigned Commands</span>
                <span className="dwa-section__badge">{filteredTasks.length}</span>
              </div>
              <div className="dwa-table-wrap">
                {filteredTasks.length === 0 ? (<div className="dwa-empty">No tasks assigned to agents yet.</div>) : (<table className="dwa-table">
                    <thead>
                      <tr>
                        <th>Command</th>
                        <th>Agent</th>
                        <th>Status</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTasks.slice(0, 15).map((t) => {
                    const s = statusBadge(taskStatusFromId(t.status_id));
                    return (<tr key={t.id} className="dwa-table__row--clickable" onClick={async () => {
                            const txId = await resolveTaskTransactionId(String(t.id));
                            navigate(txId
                                ? `${ROUTES.MOMENTUM_COMMAND}/${encodeURIComponent(txId)}`
                                : `${ROUTES.MOMENTUM_COMMAND}?taskId=${t.id}`);
                        }}>
                            <td className="dwa-table__title">
                              {t.task_number && <span className="dwa-table__task-num">{t.task_number}</span>}
                              {t.title || 'Untitled'}
                            </td>
                            <td className="dwa-table__agent">{t.agent_name}</td>
                            <td>
                              <span className="dwa-status-pill" style={{ color: s.color, background: `${s.color}14`, borderColor: `${s.color}30` }}>
                                {s.label}
                              </span>
                            </td>
                            <td className="dwa-table__time">{timeAgo(t.created_at || undefined)}</td>
                          </tr>);
                })}
                    </tbody>
                  </table>)}
              </div>
            </div>

            
            <div className="dwa-section dwa-section--runs">
              <div className="dwa-section__hdr">
                <FiRepeat size={14}/>
                <span>Routine Runs</span>
                <span className="dwa-section__badge">{filteredRuns.length}</span>
              </div>
              <div className="dwa-table-wrap">
                {filteredRuns.length === 0 ? (<div className="dwa-empty">No routine runs recorded yet.</div>) : (<table className="dwa-table">
                    <thead>
                      <tr>
                        <th>Run</th>
                        <th>Agent</th>
                        <th>Status</th>
                        <th>Duration</th>
                        <th>Started</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRuns.slice(0, 15).map((r, i) => {
                    const s = statusBadge(r.status || undefined);
                    return (<tr key={i} className={r.routine_id ? 'dwa-table__row--clickable' : ''} onClick={r.routine_id ? () => navigate(`${ROUTES.MOMENTUM_ROUTINES}/${encodeURIComponent(r.routine_id!)}`) : undefined}>
                            <td className="dwa-table__title">{r.name || `Run #${r.id}`}</td>
                            <td className="dwa-table__agent">{r.agent_name}</td>
                            <td>
                              <span className="dwa-status-pill" style={{ color: s.color, background: `${s.color}14`, borderColor: `${s.color}30` }}>
                                {s.label}
                              </span>
                            </td>
                            <td className="dwa-table__time">{fmtDuration(r.duration_sec || undefined)}</td>
                            <td className="dwa-table__time">{timeAgo(r.started_at || undefined)}</td>
                          </tr>);
                })}
                    </tbody>
                  </table>)}
              </div>
            </div>

            
            <div className="dwa-section dwa-section--chats">
              <div className="dwa-section__hdr">
                <FiMessageSquare size={14}/>
                <span>Chats</span>
                <span className="dwa-section__badge">{filteredChats.length}</span>
              </div>
              <div className="dwa-table-wrap">
                {filteredChats.length === 0 ? (<div className="dwa-empty">No chat sessions recorded yet.</div>) : (<table className="dwa-table">
                    <thead>
                      <tr>
                        <th>Chat</th>
                        <th>Agent</th>
                        <th>Started</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredChats.slice(0, 15).map((c) => (<tr key={c.id} className="dwa-table__row--clickable" onClick={() => navigate(`${ROUTES.CHAT}/${c.id}`, {
                        state: { dwAgentId: c.agent_id, dwAgentName: c.agent_name, dwSessionId: c.id },
                    })}>
                          <td className="dwa-table__title">{chatSessionTitle(c.title)}</td>
                          <td className="dwa-table__agent">{c.agent_name}</td>
                          <td className="dwa-table__time">{timeAgo(c.created || undefined)}</td>
                        </tr>))}
                    </tbody>
                  </table>)}
              </div>
            </div>

            </>)}
          </div>
        </>)}
    </div>);
};
export default DwArtifactsPage;
